import {
  Box,
  IconButton,
  MenuItem,
  Popper,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useReducer, useState } from 'react';
import { Link } from 'react-router-dom';

import ChevronIcon from '../../../packages/common/shared-ui/src/icons/ChevronIcon';
import EyeIcon from '../../../packages/common/shared-ui/src/icons/EyeIcon';
import { useTranslation } from '../../../packages/common/utils/getLocalString';
import { store } from '../app/redux';

import SpreadsheetWeekValue from './SpreadsheetWeekValue';

// Original class: tableCell
const StyledTableCell = styled(TableCell)(theme => ({
  boxShadow: '1px 1px #E4E4EF',
  width: 220,
}));
// Original class: projectSelect
const StyledProjectSelect = styled(Select)(({ theme }) => ({
  width: '100%',
  height: 56,
  padding: theme.spacing(4, 2, 4, 4),

  '& .MuiSelect-select': {
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: '0 !important',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    backgroundColor: 'transparent',
    zIndex: 1,
  },

  '& .MuiSelect-select:focus': {
    backgroundColor: 'transparent',
  },

  '& .MuiSvgIcon-root': {
    position: 'absolute',
    right: 8,
    zIndex: 0,
  },
}));
// Original class: tableCellSmall
const StyledTableCellSmall = styled(TableCell)(({ theme }) => ({
  width: 100,
  boxShadow: '0 1px #E4E4EF',

  '&.MuiTableCell-root.MuiTableCell-paddingNone': {
    padding: '0 !important',
  },

  '&.MuiTableCell-root:not(.MuiTableCell-head)': {
    padding: theme.spacing(4.5, 7),
  },
}));
// Original class: totalCell
const StyledTotalCell = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4.5, 7),
}));
// Original class: hideButton
const StyledHideButton = styled(IconButton)(theme => ({
  '&.MuiButtonBase-root': {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
  },
}));
// Original class: commentTooltip
const StyledCommentTooltip = styled(Popper)(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    marginTop: '0 !important',
    backgroundColor: '#F1F2F8',
    borderRadius: theme.spacing(4),
    padding: theme.spacing(2),
    color: '#212346',
  },
}));
// Original class: issueLink
const StyledIssueLink = styled(Link)(theme => ({
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const SpreadsheetWeek = ({
  projectIssue,
  formattedAnotherProjects,
  anotherProjects,
  handleHideWeek,
  handleChangeDayHour,
  handleChangeDayComment,
  handleSelectNewProject,
  handleSelectNewProjectTask,
  timeType,
  timeTypes,
}) => {
  const getLocalString = useTranslation();
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const tooltipText = getLocalString(
    'SpreadsheetContainer',
    'labels',
    'hideText',
  );

  const [isWeekEdited, setIsWeekEdited] = useState(false);
  const state = store.getState();

  const weekHours = (
    timeType === timeTypes.MAIN_HOURS
      ? projectIssue.weekDays
      : projectIssue.overTimeWeekDays
  ).reduce((acc, day) => {
    return day.hours + acc;
  }, 0);

  const handleUpdateDayHourWithWeekUpdate = useCallback(
    ({ currentTarget: target }) => {
      setIsWeekEdited(true);
      handleChangeDayHour(
        target.value === ''
          ? ''
          : target.value[0] === '.' || target.value[0] === ','
            ? `0${target.value}`
            : target.value,
        projectIssue.issueUniqueId,
        target.dataset.date,
        target.name,
      );
      forceUpdate();
    },
    [handleChangeDayHour, projectIssue.issueUniqueId],
  );

  const handleChangeProject = useCallback(
    e => {
      handleSelectNewProject(e.target.value, projectIssue);
      forceUpdate();
    },
    [handleSelectNewProject, projectIssue],
  );

  const handleChangeProjectTask = useCallback(
    e => {
      handleSelectNewProjectTask(e.target.value, projectIssue);
      forceUpdate();
    },
    [handleSelectNewProjectTask, projectIssue],
  );

  return (
    <TableRow key={projectIssue.issueUniqueId}>
      {`${projectIssue.projectName}`.split('')[0] === '_' ? (
        <StyledTableCell padding='none'>
          <StyledProjectSelect
            variant='standard'
            onChange={handleChangeProject}
            IconComponent={() => (
              <ChevronIcon
                direction='down'
                fontSize='small'
                viewBox='0 0 24 24'
              />
            )}
            disableUnderline
            defaultValue={0}
          >
            <MenuItem disabled value={0}>
              <Typography variant='h4'>
                {getLocalString(
                  'SpreadsheetContainer',
                  'labels',
                  'chooseProject',
                )}
              </Typography>
            </MenuItem>
            {anotherProjects.map(project => (
              <MenuItem key={project.projectId} value={project.projectId}>
                <Typography variant='h4'>{project.projectName}</Typography>
              </MenuItem>
            ))}
          </StyledProjectSelect>
        </StyledTableCell>
      ) : (
        <StyledTableCell>
          <Typography variant='h4'>{projectIssue.projectName}</Typography>
        </StyledTableCell>
      )}
      {`${projectIssue.issueName}`.split('')[0] === '_' ? (
        <StyledTableCell padding='none'>
          <StyledProjectSelect
            variant='standard'
            IconComponent={() => (
              <ChevronIcon
                direction='down'
                fontSize='small'
                viewBox='0 0 24 24'
              />
            )}
            onChange={handleChangeProjectTask}
            defaultValue={0}
            disabled={`${projectIssue.projectName}`.split('')[0] === '_'}
            disableUnderline
          >
            <MenuItem disabled value={0}>
              <Typography variant='h4'>
                {getLocalString('SpreadsheetContainer', 'labels', 'chooseTask')}
              </Typography>
            </MenuItem>
            {Object.keys(formattedAnotherProjects).map(issueKey => {
              const issue = formattedAnotherProjects[issueKey];
              return (
                projectIssue.projectId === issue.projectId && (
                  <MenuItem
                    key={issue.issueUniqueId}
                    value={issue.issueUniqueId}
                  >
                    <Typography variant='h4'>{issue.issueName}</Typography>
                  </MenuItem>
                )
              );
            })}
          </StyledProjectSelect>
        </StyledTableCell>
      ) : (
        <StyledTableCell>
          <StyledIssueLink
            to={
              state.user.project
                ? `/issues/${projectIssue.issueId}?project=${state.user.project.redmineId}`
                : `/issues/${projectIssue.issueId}`
            }
          >
            <Typography variant='h4'>{projectIssue.issueName}</Typography>
          </StyledIssueLink>
        </StyledTableCell>
      )}
      {(timeType === timeTypes.MAIN_HOURS
        ? projectIssue.weekDays
        : projectIssue.overTimeWeekDays
      ).map(day => (
        <SpreadsheetWeekValue
          key={day.date}
          day={day}
          timeType={timeType}
          projectIssue={projectIssue}
          handleUpdateDayHourWithWeekUpdate={handleUpdateDayHourWithWeekUpdate}
          handleChangeDayHour={handleChangeDayHour}
          handleChangeDayComment={handleChangeDayComment}
        />
      ))}
      <StyledTableCellSmall align='center' padding='none'>
        <StyledTotalCell>
          {parseFloat(
            Number(
              timeType === timeTypes.MAIN_HOURS
                ? projectIssue.totalNormalHours
                : projectIssue.totalOvertimeHours,
            ).toFixed(1),
          )}
          <StyledHideButton
            disableRipple
            disabled={isWeekEdited ? true : Boolean(weekHours)}
            onClick={handleHideWeek}
            issueid={projectIssue.issueUniqueId}
          >
            <Tooltip
              title={tooltipText}
              slotProps={{
                popper: { component: StyledCommentTooltip },
              }}
            >
              <Box>
                <EyeIcon
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  color={
                    isWeekEdited ? '#FAFAFC' : weekHours ? '#FAFAFC' : '#DCDFF4'
                  }
                />
              </Box>
            </Tooltip>
          </StyledHideButton>
        </StyledTotalCell>
      </StyledTableCellSmall>
    </TableRow>
  );
};

export default SpreadsheetWeek;
